import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, Printer, BarChart3, AlertTriangle, RefreshCw, Database, Cpu, MonitorSmartphone, ChevronDown, ExternalLink, Download, ShieldCheck, Flag } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { fmtDate, fmtDateOnly } from "@/lib/time";

const CFG_KEY = "reportConfig";
const loadCfg = () => { try { return JSON.parse(localStorage.getItem(CFG_KEY)) || {}; } catch { return {}; } };

const daysSince = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso.length <= 10 ? iso + "T00:00:00Z" : iso);
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
};

const csvDownload = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [headers.map(esc).join(",")].concat(rows.map((r) => r.map(esc).join(","))).join("\r\n");
  const blob = new Blob(["\ufeff" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export default function Reports() {
  const { lang } = useLang();
  const de = lang === "de";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const saved = loadCfg();
  const [dbAgeDays, setDbAgeDays] = useState(saved.dbAgeDays ?? 90);
  const [targetPayload, setTargetPayload] = useState(saved.targetPayload ?? "");
  const [targetSdk, setTargetSdk] = useState(saved.targetSdk ?? "");
  const [targetTv, setTargetTv] = useState(saved.targetTv ?? "");
  const [groupBy, setGroupBy] = useState(saved.groupBy ?? "model");
  const [openSection, setOpenSection] = useState("dbold");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/reports/dataset"); setData(r.data); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Soll-TeamViewer-Version aus den (geteilten) Server-Einstellungen laden
  useEffect(() => {
    api.get("/settings/tv-target").then((r) => { if (r.data && typeof r.data.version === "string") setTargetTv(r.data.version); }).catch(() => {});
  }, []);
  const tvSavedRef = React.useRef(null);
  useEffect(() => {
    if (tvSavedRef.current === null) { tvSavedRef.current = targetTv; return; }
    if (tvSavedRef.current === targetTv) return;
    const h = setTimeout(() => {
      api.put("/settings/tv-target", { version: (targetTv || "").trim() })
        .then(() => { tvSavedRef.current = targetTv; })
        .catch(() => {}); // kein Recht zum Speichern -> still ignorieren
    }, 600);
    return () => clearTimeout(h);
  }, [targetTv]);

  const devices = data?.devices || [];
  const latestPayload = data?.meta?.latest_payload_version;

  useEffect(() => {
    if (!devices.length) return;
    if (targetPayload === "" && latestPayload != null) setTargetPayload(String(latestPayload));
    // veralteten (niedrigeren) Sollwert automatisch auf die aktuelle Stable-Version heilen
    if (latestPayload != null && targetPayload !== "" && Number(targetPayload) < Number(latestPayload)) setTargetPayload(String(latestPayload));
    if (targetSdk === "") {
      const counts = {};
      devices.forEach((d) => { const s = (d.extra?.sdk_version || "").trim(); if (s) counts[s] = (counts[s] || 0) + 1; });
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      if (top) setTargetSdk(top[0]);
    }
  }, [devices.length]); // eslint-disable-line

  useEffect(() => {
    localStorage.setItem(CFG_KEY, JSON.stringify({ dbAgeDays, targetPayload, targetSdk, targetTv, groupBy }));
  }, [dbAgeDays, targetPayload, targetSdk, targetTv, groupBy]);

  const modelOf = (d) => (d.extra?.model_detail || d.model || "").trim() || "—";
  const sdkOf = (d) => (d.extra?.sdk_version || "").trim() || "—";
  const dbDateOf = (d) => (d.extra?.devices_dat || d.extra?.db_dat || "").slice(0, 10);
  const tvOf = (d) => !d.teamviewer_id ? (de ? "Ohne TV-ID" : "No TV-ID") : (d.tv_online === true ? "Online" : d.tv_online === false ? "Offline" : (de ? "Unbekannt" : "Unknown"));
  const condOf = (d) => ({ lager: de ? "Lager" : "Stock", defekt: "Defekt", ausgemustert: de ? "Ausgemustert" : "Retired" }[d.assignment] || (de ? "Im Einsatz" : "In use"));
  const agentOf = (d) => d.agent_installed ? (de ? "Mit Agent" : "With agent") : (de ? "Kein Agent" : "No agent");
  const dbMonthOf = (d) => { const s = dbDateOf(d); return s ? s.slice(0, 7) : "—"; };
  const ortOf = (d) => (d.extra?.Ort || "").trim() || "—";

  const isDefective = (d) => d.assignment === "defekt" || d.assignment === "ausgemustert";
  const dbFresh = (d) => { const days = daysSince(dbDateOf(d)); return days != null && days <= dbAgeDays; };
  const payloadOk = (d) => targetPayload === "" || String(d.payload_version ?? "") === String(targetPayload);
  const sdkOk = (d) => !targetSdk || (d.extra?.sdk_version || "").trim() === targetSdk;

  const health = useMemo(() => {
    const agentDevs = devices.filter((d) => d.agent_installed);
    const intact = agentDevs.filter((d) => payloadOk(d) && dbFresh(d) && sdkOk(d) && !isDefective(d));
    return {
      total: devices.length,
      withAgent: agentDevs.length,
      noAgent: devices.length - agentDevs.length,
      intact: intact.length,
      payloadOld: agentDevs.filter((d) => !payloadOk(d)).length,
      dbOld: agentDevs.filter((d) => { const x = daysSince(dbDateOf(d)); return x != null && x > dbAgeDays; }).length,
      sdkMismatch: agentDevs.filter((d) => targetSdk && (d.extra?.sdk_version || "").trim() && !sdkOk(d)).length,
      defective: devices.filter(isDefective).length,
      noTv: devices.filter((d) => !d.teamviewer_id).length,
    };
  }, [devices, dbAgeDays, targetPayload, targetSdk]);

  const GROUPS = [
    { id: "model", de: "Modell", en: "Model", fn: modelOf },
    { id: "sdk", de: "Regula SDK", en: "Regula SDK", fn: sdkOf },
    { id: "dbmonth", de: "DB-Stand (Monat)", en: "DB date (month)", fn: dbMonthOf },
    { id: "payload", de: "Payload-Version", en: "Payload version", fn: (d) => (d.payload_version ?? "—") + "" },
    { id: "agent", de: "Agent", en: "Agent", fn: agentOf },
    { id: "tv", de: "TeamViewer", en: "TeamViewer", fn: tvOf },
    { id: "condition", de: "Zustand", en: "Condition", fn: condOf },
    { id: "os", de: "Betriebssystem", en: "OS", fn: (d) => (d.os_version || "").trim() || "—" },
    { id: "city", de: "Ort", en: "City", fn: ortOf },
    { id: "group", de: "Gruppe", en: "Group", fn: (d) => (d.group || "").trim() || "—" },
  ];
  const activeGroup = GROUPS.find((g) => g.id === groupBy) || GROUPS[0];
  const breakdown = useMemo(() => {
    const m = {};
    devices.forEach((d) => { const k = activeGroup.fn(d); m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [devices, groupBy, dbAgeDays]);
  const maxCount = breakdown.reduce((mx, [, c]) => Math.max(mx, c), 0) || 1;

  const tvVerOf = (d) => (d.teamviewer_version || d.extra?.teamviewer_version || "").trim();
  const cmpVer = (a, b) => { const pa = String(a).split(/[^0-9]+/).filter(Boolean).map(Number); const pb = String(b).split(/[^0-9]+/).filter(Boolean).map(Number); for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const x = pa[i] || 0, y = pb[i] || 0; if (x !== y) return x - y; } return 0; };
  const maxTv = devices.reduce((m, d) => { const v = tvVerOf(d); return v && (!m || cmpVer(v, m) > 0) ? v : m; }, "");
  const tvTargetFixed = !!(targetTv || "").trim();
  const tvTarget = tvTargetFixed ? (targetTv || "").trim() : maxTv;

  const actions = [
    { id: "unclearcode", title: de ? "Unklarer Gerätecode (-?)" : "Unclear device code (-?)", icon: AlertTriangle, filter: (d) => (d.device_code || "").includes("?"), val: (d) => d.asset_tag || "—" },
    { id: "flagged", title: de ? "Markierte Aufgaben (manuell)" : "Flagged tasks (manual)", icon: Flag, filter: (d) => !!d.task_flag, val: (d) => d.task_note || "—" },
    { id: "dbold", title: de ? `Regula-DB veraltet (> ${dbAgeDays} Tage)` : `Regula DB outdated (> ${dbAgeDays}d)`, icon: Database, filter: (d) => d.agent_installed && (() => { const x = daysSince(dbDateOf(d)); return x != null && x > dbAgeDays; })(), val: (d) => { const s = dbDateOf(d); return s ? fmtDateOnly(s) : "—"; } },
    { id: "payload", title: de ? "Payload nicht aktuell" : "Payload not current", icon: RefreshCw, filter: (d) => d.agent_installed && !payloadOk(d), val: (d) => "v" + (d.payload_version ?? "—") },
    { id: "sdk", title: de ? "Abweichende SDK-Version" : "SDK version mismatch", icon: Cpu, filter: (d) => d.agent_installed && targetSdk && (d.extra?.sdk_version || "").trim() && !sdkOk(d), val: (d) => d.extra?.sdk_version || "—" },
    { id: "noagent", title: de ? "Kein Agent installiert" : "No agent installed", icon: MonitorSmartphone, filter: (d) => !d.agent_installed, val: () => "—" },
    { id: "notv", title: de ? "Ohne TeamViewer-ID" : "No TeamViewer ID", icon: AlertTriangle, filter: (d) => !d.teamviewer_id, val: () => "—" },
    { id: "noserial", title: de ? "Ohne Seriennummer" : "No serial number", icon: AlertTriangle, filter: (d) => !(d.tablet_serial || "").trim(), val: () => "—" },
    { id: "nomodel", title: de ? "Ohne Modell" : "No model", icon: AlertTriangle, filter: (d) => !isDefective(d) && modelOf(d) === "—", val: () => "—" },
    { id: "noloc", title: de ? "Ohne Standort-Name" : "No location name", icon: AlertTriangle, filter: (d) => !isDefective(d) && !!d.location_id && !(d.location_name || "").trim(), val: (d) => d.location_id || "—" },
    { id: "nomfg", title: de ? "Ohne Hersteller" : "No manufacturer", icon: AlertTriangle, filter: (d) => !isDefective(d) && !(d.manufacturer || "").trim(), val: () => "—" },
    { id: "nocity", title: de ? "Ohne Ort" : "No city", icon: AlertTriangle, filter: (d) => !isDefective(d) && ortOf(d) === "—", val: () => "—" },
    { id: "tvnotinaccount", title: de ? "TV-ID nicht im TeamViewer-Konto" : "TV-ID not in TeamViewer account", icon: AlertTriangle, filter: (d) => !isDefective(d) && !!(d.teamviewer_id || "").trim() && d.tv_in_account === false, val: (d) => d.teamviewer_id || "—" },
    { id: "tvstale", title: de ? "Agent online, aber TeamViewer offline" : "Agent online but TeamViewer offline", icon: AlertTriangle, filter: (d) => !isDefective(d) && d.status === "online" && d.tv_online === false, val: (d) => d.teamviewer_id || "—" },
    { id: "tvold", title: de ? `TeamViewer veraltet (${tvTargetFixed ? "Soll" : "neueste"}: ${tvTarget || "—"})` : `TeamViewer outdated (${tvTargetFixed ? "target" : "latest"}: ${tvTarget || "—"})`, icon: AlertTriangle, filter: (d) => !isDefective(d) && d.agent_installed && tvVerOf(d) && tvTarget && cmpVer(tvVerOf(d), tvTarget) < 0, val: (d) => tvVerOf(d) || "—" },
    { id: "serialmismatch", title: de ? "Seriennummer-Abweichung (Soll≠Ist)" : "Serial mismatch (target≠actual)", icon: AlertTriangle,
      filter: (d) => {
        const n = (x) => String(x || "").replace(/\s/g, "").replace(/^0+/, "").toUpperCase();
        const ex = d.extra || {};
        const scMis = ex["Scanner-SN (Soll)"] && n(ex["Scanner-SN (Soll)"]) !== n(ex.scanner_serial);
        const pcMis = ex["SN-PC (Soll)"] && n(ex["SN-PC (Soll)"]) !== n(d.tablet_serial);
        return !isDefective(d) && (scMis || pcMis);
      },
      val: (d) => { const ex = d.extra || {}; return `SC: ${ex.scanner_serial || "—"} / Soll ${ex["Scanner-SN (Soll)"] || "—"}`; } },
  ].map((a) => ({ ...a, list: devices.filter(a.filter) }));

  const exportGroup = (a) => {
    const rows = a.list.map((d) => [d.asset_tag || "", d.device_code || "", d.location_name || "", ortOf(d) === "—" ? "" : ortOf(d), a.val(d) === "—" ? "" : a.val(d)]);
    csvDownload(`report_${a.id}_${new Date().toISOString().slice(0, 10)}.csv`, ["AssetID", "Geraetecode", "Standort", "Ort", de ? "Wert" : "Value"], rows);
  };
  const exportAll = () => {
    const rows = [];
    actions.forEach((a) => a.list.forEach((d) => rows.push([a.title, d.asset_tag || "", d.device_code || "", d.location_name || "", ortOf(d) === "—" ? "" : ortOf(d), a.val(d) === "—" ? "" : a.val(d)])));
    csvDownload(`report_handlungsbedarf_${new Date().toISOString().slice(0, 10)}.csv`, [de ? "Kategorie" : "Category", "AssetID", "Geraetecode", "Standort", "Ort", de ? "Wert" : "Value"], rows);
  };

  const doPrint = () => {
    document.body.classList.add("print-report");
    const cleanup = () => { document.body.classList.remove("print-report"); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 60);
  };

  const openDev = (d) => nav(`/d/${encodeURIComponent(d.asset_tag || d.device_code || d.device_uid)}`);

  const TONE = {
    pos: "bg-emerald-50 border-emerald-200 text-emerald-700",
    warn: "bg-amber-50 border-amber-200 text-amber-700",
    danger: "bg-red-50 border-red-200 text-red-700",
  };
  const tiles = [
    { id: "intact", v: health.intact, label: de ? "Aktuell & intakt" : "Up-to-date & intact", tone: "pos" },
    { id: "agent-yes", v: health.withAgent, label: de ? "Mit Agent" : "With agent", tone: "pos" },
    { id: "payload-old", v: health.payloadOld, label: de ? "Payload veraltet" : "Payload outdated", tone: "warn" },
    { id: "regula-old", v: health.dbOld, label: de ? "Regula-DB veraltet" : "Regula DB outdated", tone: "warn" },
    { id: "sdk-diff", v: health.sdkMismatch, label: de ? "SDK abweichend" : "SDK mismatch", tone: "warn" },
    { id: "defekt", v: health.defective, label: de ? "Defekt / Ausgemustert" : "Defective / retired", tone: "danger" },
    { id: "no-agent", v: health.noAgent, label: de ? "Ohne Agent" : "No agent", tone: "danger" },
    { id: "no-tvid", v: health.noTv, label: de ? "Ohne TV-ID" : "No TV-ID", tone: "danger" },
  ];

  const totalActions = actions.reduce((s, a) => s + a.list.length, 0);

  return (
    <div className="min-h-screen bg-white text-gray-800" data-testid="flotten-report-page-container">
      <Header />
      <div className="w-full px-4 sm:px-6 py-8 report-print report-container">

        {/* Print-only executive header */}
        <div className="print-only" style={{ display: "none" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "2px solid #0F172A", paddingBottom: "8px", marginBottom: "16px" }}>
            <div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#0F172A" }}>Flotten-Report</div>
              <div style={{ fontSize: "11px", color: "#475569" }}>IT Operations · Tablet-Flotte</div>
            </div>
            <div style={{ textAlign: "right", fontSize: "11px", color: "#475569" }}>
              <div>{de ? "Erstellt" : "Generated"}: {fmtDate(data?.meta?.generated_at)}</div>
              <div>{health.total} {de ? "Geräte" : "devices"} · {de ? "DB-Schwelle" : "DB threshold"} {dbAgeDays}d · Payload v{targetPayload || "—"}</div>
            </div>
          </div>
        </div>

        {/* Screen header */}
        <div className="flex items-start justify-between gap-4 mb-2 no-print">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-3" data-testid="report-header-title">
              <BarChart3 className="w-7 h-7 text-[#c00000]" /> {de ? "Flotten-Report" : "Fleet report"}
            </h1>
            <p className="rp-sub text-sm text-gray-600 mt-1" data-testid="report-generated-timestamp">
              {de ? "IT Operations · Tablet-Flotte" : "IT Operations · Tablet fleet"} · {de ? "Stand" : "As of"} {fmtDate(data?.meta?.generated_at)} · {health.total} {de ? "Geräte" : "devices"}
            </p>
          </div>
          <button data-testid="print-report-button" onClick={doPrint} className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors shadow-lg shadow-[#c00000]/20">
            <Printer className="w-4 h-4" /> {de ? "Als PDF drucken" : "Print / PDF"}
          </button>
        </div>

        {/* Config bar (screen only) */}
        <div className="config-bar no-print bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="text-sm">
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5">{de ? "DB veraltet ab (Tage)" : "DB outdated after (days)"}</span>
            <input data-testid="config-threshold-days-input" type="number" min={1} value={dbAgeDays} onChange={(e) => setDbAgeDays(parseInt(e.target.value, 10) || 0)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 focus:border-[#c00000]/50 outline-none" />
          </label>
          <label className="text-sm">
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5">{de ? "Soll-Payload-Version" : "Target payload version"}</span>
            <div className="flex items-center gap-2">
              <input data-testid="config-target-payload-select" value={targetPayload} onChange={(e) => setTargetPayload(e.target.value)} placeholder={latestPayload != null ? `v${latestPayload}` : ""} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 font-mono focus:border-[#c00000]/50 outline-none" />
              {latestPayload != null && String(targetPayload) !== String(latestPayload) && (
                <button
                  type="button"
                  data-testid="config-target-payload-reset"
                  onClick={() => setTargetPayload(String(latestPayload))}
                  title={de ? `Auf aktuelle Stable-Version setzen (v${latestPayload})` : `Set to current stable (v${latestPayload})`}
                  className="shrink-0 whitespace-nowrap rounded-lg border border-[#c00000]/40 bg-[#c00000]/10 px-3 py-2 text-[#c00000] hover:bg-[#c00000]/20 transition-colors text-xs font-mono"
                >→ v{latestPayload}</button>
              )}
            </div>
          </label>
          <label className="text-sm">
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5">{de ? "Soll-SDK-Version" : "Target SDK version"}</span>
            <input data-testid="config-target-sdk-select" value={targetSdk} onChange={(e) => setTargetSdk(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 font-mono focus:border-[#c00000]/50 outline-none" />
          </label>
          <label className="text-sm">
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5">{de ? "Soll-TeamViewer-Version" : "Target TeamViewer version"}</span>
            <div className="flex items-center gap-2">
              <input data-testid="config-target-tv-input" value={targetTv} onChange={(e) => setTargetTv(e.target.value)} placeholder={maxTv ? `${maxTv} (${de ? "höchste" : "highest"})` : (de ? "leer = höchste im Bestand" : "empty = highest in fleet")} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 font-mono focus:border-[#c00000]/50 outline-none" />
              {tvTargetFixed && (
                <button
                  type="button"
                  data-testid="config-target-tv-reset"
                  onClick={() => setTargetTv("")}
                  title={de ? "Feste Soll-Version entfernen (wieder höchste im Bestand nutzen)" : "Clear fixed target (use fleet highest again)"}
                  className="shrink-0 whitespace-nowrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 hover:bg-gray-100 transition-colors text-xs font-mono"
                >{de ? "Auto" : "Auto"}</button>
              )}
            </div>
          </label>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> {de ? "Lädt…" : "Loading…"}</div>
        ) : (
          <>
            {/* Executive summary line */}
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-[#c00000]" />
              <h2 className="text-lg font-semibold text-gray-900">{de ? "Gesundheits-Übersicht" : "Health overview"}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="report-health">
              {tiles.map((tl) => (
                <div key={tl.id} data-testid={`kpi-tile-${tl.id}`} className={`kpi-tile tone-${tl.tone} rounded-xl px-4 py-4 border ${TONE[tl.tone]}`}>
                  <div className="kpi-num text-3xl font-bold tabular-nums leading-none">{tl.v}</div>
                  <div className="text-xs mt-2 opacity-90 font-medium">{tl.label}</div>
                </div>
              ))}
            </div>

            {/* Breakdown */}
            <div className="rp-card breakdown-card bg-gray-50 border border-gray-200 rounded-xl p-5 mb-10">
              <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#c00000]" /> {de ? "Aufschlüsselung" : "Breakdown"}</h2>
                <div className="no-print flex items-center gap-2">
                  <span className="text-xs text-gray-600">{de ? "Gruppieren nach" : "Group by"}</span>
                  <select data-testid="breakdown-dimension-selector" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 outline-none">
                    {GROUPS.map((g) => <option key={g.id} value={g.id} className="bg-white">{de ? g.de : g.en}</option>)}
                  </select>
                </div>
                <div className="print-only text-sm text-slate-600" style={{ display: "none" }}>{de ? "Gruppiert nach" : "Grouped by"}: {de ? activeGroup.de : activeGroup.en}</div>
              </div>
              <div className="space-y-2.5" data-testid="report-breakdown">
                {breakdown.map(([k, c]) => (
                  <div key={k} data-testid={`breakdown-row-${k}`} className="flex items-center gap-3">
                    <div className="rp-label w-44 shrink-0 text-sm truncate text-gray-700" title={k}>{k}</div>
                    <div className="rp-bar flex-1 h-6 bg-gray-50 rounded overflow-hidden">
                      <div className="h-full bg-[#c00000]/70 rounded" style={{ width: `${(c / maxCount) * 100}%` }} />
                    </div>
                    <div className="rp-count w-20 text-right text-sm font-mono text-gray-800">{c} <span className="text-gray-600">{Math.round((c / health.total) * 100)}%</span></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action needed */}
            <div className="mb-2 flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> {de ? "Handlungsbedarf" : "Action needed"} <span className="text-sm text-gray-600 font-normal">({totalActions})</span></h2>
              <button data-testid="csv-export-all" onClick={exportAll} className="no-print flex items-center gap-1.5 text-xs text-[#c00000] border border-[#c00000]/30 bg-[#c00000]/5 hover:bg-[#c00000]/15 rounded-lg px-3 py-1.5 transition-colors">
                <Download className="w-3.5 h-3.5" /> {de ? "Alles als CSV" : "Export all CSV"}
              </button>
            </div>
            <div className="space-y-3" data-testid="report-actions">
              {actions.map((a) => {
                const Icon = a.icon; const open = openSection === a.id;
                return (
                  <div key={a.id} className="action-group rp-card border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                    <div className="action-head flex items-center justify-between gap-3 px-4 py-3">
                      <button data-testid={`action-needed-group-${a.id}`} onClick={() => setOpenSection(open ? null : a.id)} className="flex items-center gap-2.5 text-sm text-left flex-1 min-w-0">
                        <Icon className={`w-4 h-4 shrink-0 ${a.list.length ? "text-amber-600" : "text-[#c00000]"}`} />
                        <span className="font-medium truncate">{a.title}</span>
                      </button>
                      <div className="flex items-center gap-3 shrink-0">
                        {a.list.length > 0 && (
                          <button data-testid={`csv-export-button-${a.id}`} onClick={() => exportGroup(a)} title="CSV" className="no-print flex items-center gap-1 text-xs text-gray-600 hover:text-[#c00000] border border-gray-200 hover:border-[#c00000]/30 rounded-md px-2 py-1 transition-colors">
                            <Download className="w-3.5 h-3.5" /> CSV
                          </button>
                        )}
                        <span className={`text-sm font-mono font-bold ${a.list.length ? "text-amber-700" : "text-[#c00000]"}`}>{a.list.length}</span>
                        <ChevronDown className={`no-print w-4 h-4 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`} />
                      </div>
                    </div>
                    <div className={`collapsible-content overflow-x-auto ${open ? "" : "hidden"}`}>
                      {a.list.length === 0 ? (
                        <div className="px-4 pb-3 text-xs text-gray-600">{de ? "Keine" : "None"}</div>
                      ) : (
                        <table className="w-full text-sm" data-testid={`action-table-${a.id}`}>
                          <thead>
                            <tr className="text-[11px] font-mono uppercase tracking-wider text-gray-600 border-t border-gray-200">
                              <th className="text-left font-medium px-4 py-2">AssetID</th>
                              <th className="text-left font-medium px-4 py-2">{de ? "Geräte-Code" : "Device code"}</th>
                              <th className="text-left font-medium px-4 py-2">{de ? "Standort / Ort" : "Location"}</th>
                              <th className="text-left font-medium px-4 py-2">{de ? "Wert" : "Value"}</th>
                              <th className="text-right font-medium px-4 py-2 no-print"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.list.map((d) => (
                              <tr key={d.asset_tag || d.device_code} className="border-t border-gray-100">
                                <td className="px-4 py-1.5 font-mono font-bold text-gray-900">{d.asset_tag || "—"}</td>
                                <td className="px-4 py-1.5 text-gray-700">{d.device_code || d.hostname || "—"}</td>
                                <td className="px-4 py-1.5 text-gray-600">{d.location_name || (ortOf(d) === "—" ? "—" : ortOf(d))}</td>
                                <td className="px-4 py-1.5 font-mono text-gray-700">{a.val(d)}</td>
                                <td className="px-4 py-1.5 text-right no-print">
                                  <button onClick={() => openDev(d)} className="text-[#c00000] hover:text-[#c00000]"><ExternalLink className="w-3.5 h-3.5 inline" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
